import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Hash, CheckCircle, AlertCircle, Loader2, ScanLine, Plus, Eye, EyeOff } from 'lucide-react';
import { stcTransService } from '../../../../../services/stcTransService';
import { ScanTextInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

export interface SerialCountResult {
  serialNo: string;
  /** 'confirmed' = til stede (qty=1), 'missing' = ikke funnet (qty=0), 'new' = nytt serienr (qty=1) */
  status: 'confirmed' | 'missing' | 'new';
}

interface SerialCountingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (results: SerialCountResult[]) => void;
  itemNo: string;
  itemDescription: string;
  binCode: string;
  warehouseId: string;
}

interface ExpectedSerial {
  serialNo: string;
  confirmed: boolean;
}

export const SerialCountingModal: React.FC<SerialCountingModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  itemNo,
  itemDescription,
  binCode,
  warehouseId
}) => {
  const { t } = useTranslation();
  // State
  const [expectedSerials, setExpectedSerials] = useState<ExpectedSerial[]>([]);
  const [newSerials, setNewSerials] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentInput, setCurrentInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const addInputRef = useRef<HTMLInputElement>(null);
  const prevIsOpenRef = useRef(false);

  // Reset og last data ved opening
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setExpectedSerials([]);
      setNewSerials([]);
      setError(null);
      setCurrentInput('');
      setChecking(false);
      setShowAddNew(false);
      loadExpectedSerials();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fokus add-input nar den vises
  useEffect(() => {
    if (showAddNew) {
      setTimeout(() => addInputRef.current?.focus(), 100);
    }
  }, [showAddNew]);

  const loadExpectedSerials = async () => {
    setLoading(true);
    setError(null);
    try {
      const serials = await stcTransService.getSerialNumbersAtBin(itemNo, warehouseId, binCode);
      // Alle starter som ubekreftet — operatøren scanner/klikker for å bekrefte tilstedeværelse
      setExpectedSerials(
        serials.map(s => ({ serialNo: s.serialNo, confirmed: false }))
      );
    } catch (err: any) {
      console.error('Error loading serial numbers:', err);
      setError(err.message || t('warehouse.modules.inventory.serial.loadFailed', {}, 'Kunne ikke hente serienumre'));
    } finally {
      setLoading(false);
    }
  };

  const toggleSerial = (serialNo: string) => {
    setExpectedSerials(prev =>
      prev.map(s =>
        s.serialNo === serialNo ? { ...s, confirmed: !s.confirmed } : s
      )
    );
  };

  // Skann serienummer i liste -> bekreft tilstedeværelse
  const handleScanInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentInput.trim()) {
      e.preventDefault();
      const trimmed = currentInput.trim();

      // Sjekk om det matcher et forventet serienummer
      const match = expectedSerials.find(
        s => s.serialNo.toLowerCase() === trimmed.toLowerCase()
      );
      if (match) {
        if (match.confirmed) {
          setError(t('warehouse.modules.inventory.serial.alreadyConfirmed', { '0': trimmed }, `Serienummer "${trimmed}" er allerede bekreftet`));
          setCurrentInput('');
          return;
        }
        // Bekreft serienummeret (sett til stede)
        setExpectedSerials(prev =>
          prev.map(s =>
            s.serialNo === match.serialNo ? { ...s, confirmed: true } : s
          )
        );
        setCurrentInput('');
        setError(null);
        return;
      }

      // Sjekk om det er et allerede lagt til nytt serienummer
      if (newSerials.some(s => s.toLowerCase() === trimmed.toLowerCase())) {
        setError(t('warehouse.modules.inventory.serial.alreadyAdded', { '0': trimmed }, `Serienummer "${trimmed}" er allerede lagt til`));
        return;
      }

      // Nytt serienummer - spor brukeren til "Legg til ny"-seksjonen
      setError(t('warehouse.modules.inventory.serial.notInExpected', { '0': trimmed }, `"${trimmed}" finnes ikke i forventet beholdning. Bruk "Legg til nytt" for a registrere det.`));
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Legg til nytt serienummer
  const addNewSerial = useCallback(async () => {
    const trimmed = currentInput.trim();
    if (!trimmed) return;
    setError(null);

    // Sjekk om det er et forventet serienummer
    const existsInExpected = expectedSerials.some(
      s => s.serialNo.toLowerCase() === trimmed.toLowerCase()
    );
    if (existsInExpected) {
      setError(t('warehouse.modules.inventory.serial.alreadyInExpectedList', { '0': trimmed }, `"${trimmed}" er allerede i listen over forventede serienumre`));
      return;
    }

    // Lokal duplikatsjekk
    if (newSerials.some(s => s.toLowerCase() === trimmed.toLowerCase())) {
      setError(t('warehouse.modules.inventory.serial.alreadyAdded', { '0': trimmed }, `Serienummer "${trimmed}" er allerede lagt til`));
      return;
    }

    // Backend duplikatsjekk (finnes allerede i system for denne varen?)
    setChecking(true);
    try {
      const exists = await stcTransService.checkSerialNumberExists(itemNo, trimmed);
      if (exists) {
        setError(t('warehouse.modules.inventory.serial.alreadyRegisteredOtherLocation', { '0': trimmed, '1': itemNo }, `Serienummer "${trimmed}" er allerede registrert for vare ${itemNo} (annen lokasjon)`));
        setChecking(false);
        return;
      }
    } catch (err) {
      console.error('Feil ved serienummersjekk:', err);
      // Tillat offline-registrering
    }
    setChecking(false);

    setNewSerials(prev => [...prev, trimmed]);
    setCurrentInput('');
    setTimeout(() => addInputRef.current?.focus(), 50);
  }, [currentInput, expectedSerials, newSerials, itemNo, t]);

  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addNewSerial();
    }
  };

  const removeNewSerial = (index: number) => {
    setNewSerials(prev => prev.filter((_, i) => i !== index));
  };

  // Bekreft telling
  const handleConfirm = () => {
    const results: SerialCountResult[] = [];

    // Forventede serienumre
    for (const s of expectedSerials) {
      results.push({
        serialNo: s.serialNo,
        status: s.confirmed ? 'confirmed' : 'missing'
      });
    }

    // Nye serienumre
    for (const sn of newSerials) {
      results.push({ serialNo: sn, status: 'new' });
    }

    onComplete(results);
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  const confirmedCount = expectedSerials.filter(s => s.confirmed).length;
  const missingCount = expectedSerials.filter(s => !s.confirmed).length;
  const newCount = newSerials.length;
  const totalCount = confirmedCount + newCount;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-0 sm:p-4"
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={stopPropagation}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-teal-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-teal-100">
              <Hash className="text-teal-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('warehouse.modules.inventory.serial.title', {}, 'Serienummer-telling')}
              </h2>
              <p className="text-sm text-gray-500">{itemNo} - {itemDescription}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Info */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>{t('warehouse.modules.inventory.serial.locationLabel', {}, 'Lokasjon:')} <span className="font-medium text-gray-900">{binCode}</span></span>
            <span>{t('warehouse.modules.inventory.serial.expectedLabel', {}, 'Forventet:')} <span className="font-medium text-gray-900">{expectedSerials.length}</span></span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {t('warehouse.modules.inventory.serial.countHint', {}, 'Skann eller klikk på serienumre for å bekrefte at de er til stede.')}
          </p>
        </div>

        {/* Skann-input for rask bekreftelse */}
        <div className="px-4 pb-3">
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <ScanTextInput
              ref={inputRef}
              value={currentInput}
              onChange={(e) => { setCurrentInput(e.target.value); setError(null); }}
              onKeyDown={handleScanInput}
              placeholder={t('warehouse.modules.inventory.serial.scanPlaceholder', {}, 'Skann serienummer for rask bekreftelse...')}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          {error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Forventede serienumre */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
              <Loader2 className="animate-spin" size={20} />
              {t('warehouse.modules.inventory.serial.loading', {}, 'Henter serienumre...')}
            </div>
          ) : expectedSerials.length === 0 && newSerials.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <Hash size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('warehouse.modules.inventory.serial.noneFound', {}, 'Ingen serienumre funnet for denne varen på lokasjonen')}</p>
              <p className="text-xs mt-1">{t('warehouse.modules.inventory.serial.useAddNew', {}, 'Bruk "Legg til nytt" for å registrere nye serienumre')}</p>
            </div>
          ) : (
            <>
              {/* Forventet-liste med checkboxes */}
              {expectedSerials.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    {t('warehouse.modules.inventory.serial.expectedStock', { '0': expectedSerials.length }, `Forventet beholdning (${expectedSerials.length})`)}
                  </div>
                  {expectedSerials.map((serial) => (
                    <button
                      key={serial.serialNo}
                      onClick={() => toggleSerial(serial.serialNo)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
                        serial.confirmed
                          ? 'border-green-400 bg-green-50 ring-1 ring-green-200'
                          : 'border-red-300 bg-red-50 ring-1 ring-red-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          serial.confirmed ? 'bg-green-500 border-green-500' : 'border-red-300 bg-white'
                        }`}>
                          {serial.confirmed && <CheckCircle className="text-white" size={14} />}
                        </div>
                        <span className="font-mono font-medium text-gray-900">{serial.serialNo}</span>
                      </div>
                      <span className={`text-xs font-medium ${
                        serial.confirmed ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {serial.confirmed ? t('warehouse.modules.inventory.serial.present', {}, 'Til stede') : t('warehouse.modules.inventory.serial.missing', {}, 'Mangler')}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Nye serienumre */}
              {newSerials.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  <div className="text-xs font-medium text-purple-600 uppercase tracking-wider mb-2">
                    {t('warehouse.modules.inventory.serial.newSerials', { '0': newSerials.length }, `Nye serienumre (${newSerials.length})`)}
                  </div>
                  {newSerials.map((sn, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg border border-purple-300 bg-purple-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded bg-purple-500 border-2 border-purple-500 flex items-center justify-center">
                          <Plus className="text-white" size={12} />
                        </div>
                        <span className="font-mono font-medium text-gray-900">{sn}</span>
                      </div>
                      <button
                        onClick={() => removeNewSerial(index)}
                        className="text-purple-400 hover:text-red-500 text-xs transition-colors"
                      >
                        {t('common.buttons.remove', {}, 'Fjern')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Legg til nytt seksjon */}
          <div className="mt-3 border-t pt-3">
            <button
              onClick={() => setShowAddNew(!showAddNew)}
              className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors"
            >
              {showAddNew ? <EyeOff size={16} /> : <Eye size={16} />}
              {showAddNew ? t('warehouse.modules.inventory.serial.hide', {}, 'Skjul') : t('warehouse.modules.inventory.serial.addNew', {}, 'Legg til nytt serienummer')}
            </button>
            {showAddNew && (
              <div className="mt-2 flex gap-2">
                <div className="relative flex-1">
                  <Plus className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400" size={16} />
                  <ScanTextInput
                    ref={addInputRef}
                    value={showAddNew ? currentInput : ''}
                    onChange={(e) => { setCurrentInput(e.target.value); setError(null); }}
                    onKeyDown={handleAddKeyDown}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder={t('warehouse.modules.inventory.serial.scanNewPlaceholder', {}, 'Skann nytt serienummer...')}
                    disabled={checking}
                    className="w-full pl-9 pr-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm disabled:bg-gray-100"
                  />
                </div>
                <button
                  onClick={addNewSerial}
                  disabled={!currentInput.trim() || checking}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                >
                  {checking ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer med oppsummering */}
        <div className="p-4 border-t bg-gray-50">
          {/* Status-oversikt */}
          <div className="flex items-center gap-4 mb-3 text-xs">
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle size={12} /> {t('warehouse.modules.inventory.batch.confirmedCount', { '0': confirmedCount }, `Bekreftet: ${confirmedCount}`)}
            </span>
            {missingCount > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertCircle size={12} /> {t('warehouse.modules.inventory.batch.missingCount', { '0': missingCount }, `Mangler: ${missingCount}`)}
              </span>
            )}
            {newCount > 0 && (
              <span className="flex items-center gap-1 text-purple-600">
                <Plus size={12} /> {t('warehouse.modules.inventory.batch.newCount', { '0': newCount }, `Nye: ${newCount}`)}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.buttons.cancel', {}, 'Avbryt')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={expectedSerials.length === 0 && newSerials.length === 0}
              className="px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 bg-teal-600 text-white hover:bg-teal-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <CheckCircle size={16} />
              {t('warehouse.modules.inventory.batch.confirmCountButton', { '0': totalCount + missingCount }, `Bekreft telling (${totalCount + missingCount} linjer)`)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
